import { useEffect, useCallback, useState, useRef } from "react";
import { useBreakpoint } from "@core/hooks/useBreakpoint";
import { PanelTitle } from "@themes/pollinator/ui/PanelTitle";
import { StepActions } from "@themes/pollinator/ui/StepActions";
import { Loader } from "@themes/pollinator/ui/Loader";
import type { OnboardingStep, StepState } from "@core/features/onboarding";
import { OnboardingStepper } from "../Stepper";
import { StepContent } from "../StepContent";
import type { StepValidationState } from "../../onboarding.types";

const defaultStepState: StepValidationState = {
  isValid: true,
  isDirty: false,
  isSubmitting: false,
  canProceed: false,
};

export type Props = {
  /** The program ID */
  programId: number;
  /** The current onboarding step */
  currentStep: OnboardingStep;
  /** The current step index (0-based) */
  effectiveStepIndex: number;
  /** All onboarding steps */
  steps: OnboardingStep[];
  /** Whether this is the last step */
  isLastStep: boolean;
  /** Whether the current step can be skipped */
  canSkip: boolean;
  /** The state of each step (completed, skipped, current, upcoming) */
  stepStates: StepState[];
  /** Callback when the step is completed */
  onStepComplete: () => void;
  /** Callback when the skip button is clicked */
  onSkipClick: () => void;
  /** Callback when a step in the stepper is clicked */
  onStepClick: (stepIndex: number) => void;
  /** Callback when the back button is clicked */
  onBackClick: () => void;
};

/**
 * Inner component that manages step validation state.
 * Handles step content and footer with validation state.
 */
export function OnboardingContent({
  programId,
  currentStep,
  effectiveStepIndex,
  steps,
  isLastStep,
  canSkip,
  stepStates,
  onStepComplete,
  onSkipClick,
  onStepClick,
  onBackClick,
}: Props) {
  const isMdScreen = useBreakpoint("md");

  // Local state for step validation (replaces context)
  const [stepState, setStepState] = useState<StepValidationState>(defaultStepState);
  const submitRef = useRef<(() => Promise<void> | void) | null>(null);

  // Track transitioning state to keep loading overlay until step changes
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Reset step state and transitioning when step changes
  useEffect(() => {
    setStepState(defaultStepState);
    setIsTransitioning(false);
    submitRef.current = null;
  }, [effectiveStepIndex]);

  // Handle step state changes from child
  const handleStepStateChange = useCallback((state: StepValidationState) => {
    setStepState(state);
  }, []);

  // Handle primary button click - trigger step submit
  const handlePrimaryClick = useCallback(async () => {
    if (submitRef.current) {
      // Only set transitioning if step doesn't handle its own loading
      if (!stepState.handlesOwnLoading) {
        setIsTransitioning(true);
      }
      await submitRef.current();
    }
    // Note: onStepComplete is called by the step's onComplete callback after successful submission
    // isTransitioning will be reset when effectiveStepIndex changes
  }, [stepState.handlesOwnLoading]);

  // Handle back button click - use step's internal back if available, otherwise go to previous step
  const handleBackClick = useCallback(() => {
    if (stepState.onBack) {
      // Step has internal back navigation (e.g., quiz with multiple questions)
      stepState.onBack();
    } else {
      // Go to previous onboarding step
      onBackClick();
    }
  }, [stepState.onBack, onBackClick]);

  // Show loading overlay when submitting (always) or transitioning to next step
  // Transitioning overlay only shows if step doesn't handle its own loading UI
  // This means quiz shows loading during API submission, but not during its internal animation
  const showLoadingOverlay = stepState.isSubmitting || (!stepState.handlesOwnLoading && isTransitioning);

  return (
    <>
      <PanelTitle
        title="Welcome!"
        description="Complete the following steps to get started."
        isInverted={isMdScreen}
      >
        <OnboardingStepper
          stepStates={stepStates}
          canSkip={false}
          isLastStep={isLastStep}
          onStepClick={onStepClick}
          className="hidden md:block"
        />
      </PanelTitle>
      <div className="flex-1 flex flex-col bg-brand-50 text-brand-800">
        {/* Main content area with loading overlay */}
        <main className="flex-1 flex flex-col items-center p-6 md:p-8 overflow-y-auto relative">
          <div className="w-full max-w-[600px] flex-1 flex flex-col">
            <StepContent
              step={currentStep}
              programId={programId}
              onComplete={onStepComplete}
              onSkip={canSkip ? onSkipClick : undefined}
              showActionButtons={false}
              onStepStateChange={handleStepStateChange}
              submitRef={submitRef}
            />
          </div>
          {/* Loading overlay - covers content area when submitting or transitioning */}
          {showLoadingOverlay && (
            <div className="absolute inset-0 bg-brand-50/80 flex items-center justify-center z-10">
              <Loader />
            </div>
          )}
        </main>

        {/* Footer with StepActions - uses step validation state */}
        {/* Only show progress for quiz steps, using quiz's internal progress */}
        <StepActions
          stepIndex={stepState.quizStepIndex ?? effectiveStepIndex}
          totalSteps={stepState.quizTotalSteps ?? steps.length}
          showProgress={stepState.quizTotalSteps !== undefined}
          primaryLabel={stepState.primaryLabel || (isLastStep ? "Complete" : "Continue")}
          onPrimaryClick={handlePrimaryClick}
          isPrimaryDisabled={!stepState.canProceed}
          isPrimaryLoading={stepState.isSubmitting}
          onBack={effectiveStepIndex > 0 || stepState.canGoBack ? handleBackClick : undefined}
          onSkip={canSkip ? onSkipClick : undefined}
        />
      </div>
    </>
  );
}

export default OnboardingContent;
